import {Store} from 'redux';
import runtime from 'serviceworker-webpack-plugin/lib/runtime';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {isElectron} from '../../../../libs/shared/helpers/src/environment/platformHelpers';
import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {AppState} from '../../../store/appState';
import {getHasServiceWorker} from '../../../store/featureFlags/featureFlagsSelectors';
import {reloadBrowser} from '../../browser';
import {debug} from '../../debug';
import {setupCriticalUpdate} from '../../updater/criticalUpdate';
import {setupIdleUpdate} from '../../updater/idleUpdate';
import {MessageToClient} from '../serviceWorkerMessageTypes';
import {setCurrentRegistration, unregisterServiceWorker} from './serviceWorkerBridge';

export async function initializeServiceWorker() {
  // Abort if service workers aren't supported.
  const isServiceWorkerSupported = 'serviceWorker' in globalWindow.navigator;
  if (!isServiceWorkerSupported) {
    return;
  }

  // Reload whenever the controller changes, except if we go from
  // uncontrolled to controlled (which will happen on an initial load).
  let previousController = globalWindow.navigator.serviceWorker.controller;
  globalWindow.navigator.serviceWorker.addEventListener('controllerchange', () => {
    debug('serviceWorker', 'Client controllerchange');
    if (previousController) {
      reloadBrowser();
    } else {
      previousController = globalWindow.navigator.serviceWorker.controller;
    }
  });

  // Get the registration of any installed workers.
  const registration = await getRegistrationAsync();
  if (!registration) {
    return;
  }

  const waitingWorker = registration.waiting;
  if (waitingWorker) {
    if (isElectron()) {
      // On Electron, force a service worker update if there's one waitng when this page first loads.
      debug('serviceWorker', 'Force activate waiting worker on electron.');
      waitingWorker.postMessage({type: 'SKIP_WAITING'});
    } else {
      // On Web, force a service worker update if there's one tab and a waiting worker when this page loads.
      debug('serviceWorker', 'Worker is waiting. Maybe activate it.');
      waitingWorker.postMessage({type: 'SKIP_WAITING_IF_ONE_CLIENT'});
    }
  }
}

export async function setupServiceWorker(store: Store<AppState>) {
  const isServiceWorkerSupported = 'serviceWorker' in globalWindow.navigator;
  if (!isServiceWorkerSupported) {
    return;
  }

  const initialState = store.getState();
  const isServiceWorkerEnabled = getHasServiceWorker(initialState);

  if (isServiceWorkerEnabled) {
    const registration = await registerWorkerAsync();
    if (!registration) {
      return;
    }

    setCurrentRegistration(registration);
    globalWindow.navigator.serviceWorker.addEventListener('message', (event) => {
      const message = event.data as MessageToClient;
      switch (message.type) {
        case 'DEBUG':
          printDebugMessage(message.payload);
          break;
        case 'ERROR':
          reportError(message.error, message.metadata);
          break;
        case 'PROXY_DEACTIVATE':
          proxyDeactivate(registration);
          break;
        default:
          reportToBugsnag(new Error('Received Unexpected Message from Service Worker'), {
            metadata: {message},
          });
          break;
      }
    });

    // After a shift-reload, we are uncontrolled even with an active
    // worker. Tell the active worker to control us so that we can
    // reload on the next controller change.
    if (registration.active && !globalWindow.navigator.serviceWorker.controller) {
      registration.active.postMessage({type: 'CLAIM_CLIENTS'});
    }

    // Check for service worker updates every 5 min.
    setInterval(async () => {
      try {
        // Don't update the worker if there's one already installing.
        if (registration.installing) {
          return;
        }

        await registration.update();
      } catch (error) {
        if (shouldReportServiceWorkerError(error)) {
          reportToBugsnag(new ExtendedError('Unable to update service worker', error));
        }
      }
    }, 5 * 60 * 1000);

    setupIdleUpdate();
    setupCriticalUpdate(store);
  } else {
    unregisterServiceWorker();
  }
}

async function getRegistrationAsync(): Promise<ServiceWorkerRegistration | null> {
  try {
    return (await globalWindow.navigator.serviceWorker.getRegistration()) || null;
  } catch (error) {
    if (shouldReportServiceWorkerError(error)) {
      reportToBugsnag(new ExtendedError('Unable to get service worker registration', error));
    }

    return null;
  }
}

async function registerWorkerAsync(): Promise<ServiceWorkerRegistration | null> {
  try {
    return await runtime.register();
  } catch (error) {
    if (shouldReportServiceWorkerError(error)) {
      reportToBugsnag(new ExtendedError('Unable to register service worker', error));
    }

    return null;
  }
}

function shouldReportServiceWorkerError(error: any) {
  // Don't report the error if it's a network error.
  if (
    error instanceof Error &&
    error.message.includes('An unknown error occurred when fetching the script.')
  ) {
    return false;
  }

  // On Firefox, service worker methods fail if the browser is set to clear data at the end of the session.
  if (error instanceof Error && error.message.includes('The operation is insecure.')) {
    return false;
  }

  // This can happen if update() is called during initial script evaluation.
  // https://chromium.googlesource.com/chromium/src.git/+/62.0.3178.1/content/browser/service_worker/service_worker_dispatcher_host.cc
  if (error instanceof Error && error.name === 'InvalidStateError') {
    return false;
  }

  return true;
}

function reportError(error: string, metadata?: Object) {
  reportToBugsnag({name: 'ServiceWorkerError', message: error}, metadata && {metadata});
}

function printDebugMessage(payload: Array<any>) {
  const [version, ...args] = payload;
  debug('serviceWorker', ...args, `(Version ${version})`);
}

function proxyDeactivate(registration: ServiceWorkerRegistration) {
  const activeWorker = registration.active;
  if (activeWorker) {
    activeWorker.postMessage({type: 'DEACTIVATE'});
  }
}
